// Message thread for one client (SPEC §4 Inbox/MessageThread) — Project Blue
// (iMessage with automatic SMS fallback). Bubbles in/out with timestamps,
// per-service coloring (iMessage blue / SMS-RCS green), delivery-status ticks,
// compose box that sends through the worker (lib/api sendSms), mark_thread_read
// RPC when unread inbound is on screen, and a 15s poll so replies appear
// without a refresh. The header shows a small iMessage-availability dot
// (checkIMessage, fetched once per open, non-blocking).
//
// The exported Props interface is the fixed contract with ClientDetail — do
// not change it.

import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { checkIMessage, sendSms } from "../lib/api";
import { useQuery } from "../lib/useQuery";
import { fmtDateTime } from "../lib/dates";
import { phoneDisplay } from "../lib/format";
import type { Id, Message } from "../lib/types";

export interface Props {
  clientId: Id;
  phone: string | null;
  optedOut: boolean;
  compose?: boolean;
}

const POLL_MS = 15_000;
const THREAD_LIMIT = 500;

/**
 * Delivery indicator for outbound messages (Project Blue status vocabulary):
 * queued '…', sent '✓', delivered '✓✓', failed '✕ + error'. 'received' is an
 * inbound-only status and never rendered here.
 */
function DeliveryStatus({ status, error }: { status: string | null; error: string | null }) {
  const s = (status ?? "").toLowerCase();
  if (s === "failed") {
    return (
      <span className="status-failed" title={error ?? s}>
        ✕{error ? ` ${error}` : " failed"}
      </span>
    );
  }
  if (s === "delivered") {
    return (
      <span className="status-delivered" title="Delivered">
        ✓✓
      </span>
    );
  }
  if (s === "sent") {
    return <span title="Sent">✓</span>;
  }
  if (s === "queued" || s) {
    // queued (or an unknown-but-present transitional status)
    return <span title={`Status: ${s}`}>…</span>;
  }
  return null;
}

/** Outbound bubble color class by delivery service (SMS/RCS green, iMessage blue). */
function serviceClass(m: Message): string {
  if (m.service === "iMessage") return " bubble-imessage";
  if (m.service === "SMS" || m.service === "RCS") return " bubble-sms";
  return ""; // null / pending → current accent
}

function Bubble({ m }: { m: Message }) {
  const out = m.direction === "out";
  return (
    <div className={out ? `bubble bubble-out${serviceClass(m)}` : "bubble bubble-in"}>
      {m.body}
      <div className="bubble-meta">
        {fmtDateTime(m.created_at)}
        {m.service && <span className="service-label"> · {m.service}</span>}
        {out && (
          <>
            {" · "}
            <DeliveryStatus status={m.status} error={m.error} />
          </>
        )}
        {out && m.campaign_id != null && <span className="muted"> · campaign</span>}
      </div>
    </div>
  );
}

export default function MessageThread({ clientId, phone, optedOut, compose }: Props) {
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  // Messages returned by sendSms before the next poll picks them up.
  const [echoes, setEchoes] = useState<Message[]>([]);
  // iMessage availability for the header dot: null = unknown (loading/failed).
  const [imsgAvailable, setImsgAvailable] = useState<boolean | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // One non-blocking availability check per thread open; failures stay "unknown".
  useEffect(() => {
    let cancelled = false;
    setImsgAvailable(null);
    if (!phone) return;
    checkIMessage(phone).then(
      (r) => {
        if (!cancelled) setImsgAvailable(r.isIMessageAvailable);
      },
      () => {
        // Non-blocking: the thread works fine without the dot.
      },
    );
    return () => {
      cancelled = true;
    };
  }, [phone]);

  const { data, error, loading, reload } = useQuery<Message[]>(async () => {
    // Newest first + limit so a very long thread still shows its latest
    // messages; reversed below for chronological rendering.
    const { data: rows, error: qErr } = await supabase
      .from("messages")
      .select("*")
      .eq("client_id", clientId)
      .order("created_at", { ascending: false })
      .limit(THREAD_LIMIT);
    if (qErr) throw new Error(qErr.message);
    return ((rows ?? []) as Message[]).reverse();
  }, [clientId]);

  // 15s polling (SPEC §4) — reload is stable, so this runs once per mount.
  useEffect(() => {
    const t = window.setInterval(reload, POLL_MS);
    return () => window.clearInterval(t);
  }, [reload]);

  // Merge optimistic echoes that the poll hasn't returned yet.
  const messages = useMemo(() => {
    const base = data ?? [];
    if (echoes.length === 0) return base;
    const seen = new Set(base.map((m) => String(m.id)));
    const extra = echoes.filter((m) => !seen.has(String(m.id)));
    return extra.length === 0 ? base : [...base, ...extra];
  }, [data, echoes]);

  // Drop echoes once the server copy is in the fetched data.
  useEffect(() => {
    if (echoes.length === 0 || !data) return;
    const seen = new Set(data.map((m) => String(m.id)));
    if (echoes.some((m) => seen.has(String(m.id)))) {
      setEchoes((prev) => prev.filter((m) => !seen.has(String(m.id))));
    }
  }, [data, echoes]);

  // Mark unread inbound as read whenever the open thread shows any
  // (covers both opening the thread and replies arriving via the poll).
  const hasUnread = useMemo(
    () => (data ?? []).some((m) => m.direction === "in" && m.read_at == null),
    [data],
  );
  useEffect(() => {
    if (!hasUnread) return;
    void supabase.rpc("mark_thread_read", { p_client_id: clientId }).then(({ error: rpcErr }) => {
      if (rpcErr) console.error("mark_thread_read failed:", rpcErr.message);
    });
  }, [hasUnread, clientId]);

  // Keep the view pinned to the newest message.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, loading]);

  // ?compose=1 → focus the compose box straight away.
  useEffect(() => {
    if (compose && !optedOut && phone) textareaRef.current?.focus();
  }, [compose, optedOut, phone]);

  async function handleSend() {
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    setSendError(null);
    try {
      const sent = await sendSms(clientId, body);
      if (sent && sent.id != null) setEchoes((prev) => [...prev, sent]);
      setDraft("");
      reload();
      textareaRef.current?.focus();
    } catch (e) {
      setSendError(e instanceof Error ? e.message : String(e));
    } finally {
      setSending(false);
    }
  }

  const canSend = !optedOut && !!phone;

  return (
    <div className="message-thread">
      {phone && (
        <div className="thread-header">
          <span
            className={
              "imsg-dot" +
              (imsgAvailable === null ? "" : imsgAvailable ? " imsg-yes" : " imsg-no")
            }
            title={
              imsgAvailable === null
                ? "Checking iMessage availability…"
                : imsgAvailable
                  ? "iMessage available — sends arrive as blue-bubble iMessages"
                  : "No iMessage — sends fall back to SMS"
            }
            aria-label={
              imsgAvailable === null
                ? "iMessage availability unknown"
                : imsgAvailable
                  ? "iMessage available"
                  : "SMS only"
            }
          />
          <span className="muted small">
            {phoneDisplay(phone)}
            {imsgAvailable !== null && (imsgAvailable ? " · iMessage" : " · SMS")}
          </span>
        </div>
      )}

      {error && <div className="error-banner">Failed to load messages: {error}</div>}

      <div className="thread" ref={scrollRef} style={{ maxHeight: "56vh", minHeight: 120 }}>
        {loading && messages.length === 0 ? (
          <div className="loading">
            <span className="spinner" /> Loading messages…
          </div>
        ) : messages.length === 0 ? (
          <div className="empty-state">
            No messages yet{phone ? ` with ${phoneDisplay(phone)}` : ""}.
          </div>
        ) : (
          messages.map((m) => <Bubble key={String(m.id)} m={m} />)
        )}
      </div>

      {sendError && <div className="error-banner">Send failed: {sendError}</div>}

      {optedOut ? (
        <div className="opt-out-note">
          This client has opted out of SMS (replied STOP) — sending is disabled. They can text
          START to opt back in.
        </div>
      ) : !phone ? (
        <div className="muted small" style={{ paddingTop: 8 }}>
          No phone number on file — SMS unavailable for this client.
        </div>
      ) : (
        <div className="compose">
          <textarea
            ref={textareaRef}
            value={draft}
            placeholder={`Text ${phoneDisplay(phone)}…`}
            disabled={sending}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void handleSend();
              }
            }}
            aria-label="Message body"
          />
          <button
            type="button"
            className="btn btn-primary"
            disabled={sending || draft.trim().length === 0 || !canSend}
            onClick={() => void handleSend()}
          >
            {sending ? "Sending…" : "Send"}
          </button>
        </div>
      )}
    </div>
  );
}
